/**
 * Dialog for editing quiz title and description
 */

import React, { useState, useEffect } from 'react';
import './QuestionDialogs.css';

interface EditTitleDialogProps {
    isOpen: boolean;
    onClose: () => void;
    quizId: number;
    initialTitle: string;
    initialDescription: string;
    initialIsPublic: boolean;
}

export default function EditTitleDialog({
    isOpen,
    onClose,
    quizId,
    initialTitle,
    initialDescription,
    initialIsPublic,
}: EditTitleDialogProps) {
    const [title, setTitle] = useState(initialTitle);
    const [description, setDescription] = useState(initialDescription);
    const [isPublic, setIsPublic] = useState(initialIsPublic);

    useEffect(() => {
        if (isOpen) {
            setTitle(initialTitle);
            setDescription(initialDescription);
            setIsPublic(initialIsPublic);
        }
    }, [isOpen, initialTitle, initialDescription, initialIsPublic]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (title.trim()) {
            try {
                const response = await fetch(`/api/quizzes/${quizId}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ title, description, isPublic }),
                });

                if (response.ok) {
                    onClose();
                }
            } catch (error) {
                console.error('Failed to update quiz:', error);
            }
        }
    };

    const handleOverlayClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="overlay" onClick={handleOverlayClick}>
            <div className="dialog">
                <div className="header">
                    <h2 className="title">Edit Quiz Details</h2>
                    <button className="closeButton" onClick={onClose} type="button">
                        ×
                    </button>
                </div>
                <form onSubmit={handleSubmit} className="form">
                    <div className="formGroup">
                        <label htmlFor="title" className="label">
                            Quiz Title:
                        </label>
                        <input
                            id="title"
                            name="title"
                            type="text"
                            className="input"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Enter quiz title..."
                            required
                        />
                    </div>
                    <div className="formGroup">
                        <label htmlFor="description" className="label">
                            Description:
                        </label>
                        <textarea
                            id="description"
                            name="description"
                            className="textarea"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Enter quiz description..."
                        />
                    </div>
                    <div className="formGroup checkboxGroup">
                        <input
                            id="isPublic"
                            name="isPublic"
                            type="checkbox"
                            checked={isPublic}
                            onChange={(e) => setIsPublic(e.target.checked)}
                        />
                        <label htmlFor="isPublic" className="checkboxLabel">
                            Make this quiz available in the public/global registry
                        </label>
                    </div>
                    <div className="buttonGroup">
                        <button type="button" className="cancelButton" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className="submitButton">
                            Save Changes
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
